import React from 'react'
import PropTypes from 'prop-types'

import Loading from '../../../utilities/loading'
import {
  fetchFailureType,
  loadCaptureVideo,
  updateFailureType,
} from './diagnosticAssets'

const saveStatusMessages = {
  saving: 'Saving…',
  saved: 'Saved',
  error: 'Could not save. Try again.',
}

const sessionSaveStatusMessages = {
  ...saveStatusMessages,
  saved: 'Saved — loading next',
}

const failureTypeShape = PropTypes.shape({
  value: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  group: PropTypes.string,
})

export const groupFailureTypes = (failureTypes) => {
  const groups = []
  const indexByLabel = new Map()

  failureTypes.forEach((type) => {
    const groupLabel = type.group || 'Other'
    let group = indexByLabel.get(groupLabel)
    if (!group) {
      group = { label: groupLabel, types: [] }
      indexByLabel.set(groupLabel, group)
      groups.push(group)
    }
    group.types.push(type)
  })

  return groups
}

const FailureTypeStatus = ({ status, statusMessages }) =>
  status && status !== 'idle' ? (
    <div
      className={`mt-2 ${
        status === 'error' ? 'text-danger' : 'snap-failures-status'
      }`}
      aria-live="polite"
    >
      {statusMessages[status]}
    </div>
  ) : null

export const FailureTypeSelect = ({
  failureType,
  failureTypes,
  onChange,
  disabled,
  status,
  label = 'Failure classification',
  hint = 'Choose the primary issue visible in the capture video. You can revise or clear the label later.',
  emptyOptionLabel = 'Not reviewed',
  selectClassName = 'form-control',
  statusMessages = saveStatusMessages,
  layout = 'select',
}) => {
  const groups = groupFailureTypes(failureTypes)

  return (
    <div className="form-group mb-0">
      <div className="font-weight-bold mb-1" id="scan-failure-type-label">
        {label}
      </div>
      {hint ? <div className="snap-failures-hint mb-2">{hint}</div> : null}
      {layout === 'buttons' ? (
        <div
          className="snap-failures-choices"
          role="group"
          aria-labelledby="scan-failure-type-label"
        >
          {groups.map((group) => (
            <div key={group.label} className="snap-failures-group">
              <div className="snap-failures-group-label">{group.label}</div>
              {group.types.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  className={`snap-failures-choice${
                    failureType === type.value ? ' is-selected' : ''
                  }`}
                  disabled={disabled}
                  aria-pressed={failureType === type.value}
                  onClick={() => onChange(type.value)}
                >
                  {type.label}
                </button>
              ))}
            </div>
          ))}
        </div>
      ) : (
        <select
          id="scan-failure-type"
          className={selectClassName}
          value={failureType || ''}
          disabled={disabled}
          aria-labelledby="scan-failure-type-label"
          onChange={(event) => onChange(event.target.value || null)}
        >
          <option value="">{emptyOptionLabel}</option>
          {groups.map((group) => (
            <optgroup key={group.label} label={group.label}>
              {group.types.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      )}
      <FailureTypeStatus status={status} statusMessages={statusMessages} />
    </div>
  )
}

FailureTypeSelect.propTypes = {
  failureType: PropTypes.string,
  failureTypes: PropTypes.arrayOf(failureTypeShape).isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  status: PropTypes.oneOf(['idle', 'saving', 'saved', 'error']),
  label: PropTypes.string,
  hint: PropTypes.string,
  emptyOptionLabel: PropTypes.string,
  selectClassName: PropTypes.string,
  statusMessages: PropTypes.object,
  layout: PropTypes.oneOf(['select', 'buttons']),
}

export default function CaptureVideo({
  scanPackageId,
  onFailureTypeSaved,
  variant = 'detail',
  secondaryAction,
}) {
  const [videoUrl, setVideoUrl] = React.useState()
  const [videoError, setVideoError] = React.useState(false)
  const [failureReview, setFailureReview] = React.useState()
  const [saveStatus, setSaveStatus] = React.useState('idle')
  const saveRequest = React.useRef(0)
  const revokeVideoUrl = React.useRef(null)
  const playbackRetries = React.useRef(0)
  const isSession = variant === 'session'

  const clearVideoObjectUrl = () => {
    if (revokeVideoUrl.current) {
      revokeVideoUrl.current()
      revokeVideoUrl.current = null
    }
  }

  const applyVideoSource = (source) => {
    clearVideoObjectUrl()
    if (!source) {
      setVideoUrl(null)
      return
    }
    revokeVideoUrl.current = source.revoke || null
    setVideoUrl(source.url)
  }

  const loadVideo = (forceRefresh = false) => {
    setVideoUrl()
    setVideoError(false)
    return loadCaptureVideo(scanPackageId, window.fetch, { forceRefresh })
      .then((source) => {
        applyVideoSource(source)
      })
      .catch(() => {
        applyVideoSource(null)
        setVideoError(true)
      })
  }

  React.useEffect(() => {
    let active = true
    playbackRetries.current = 0
    setFailureReview()
    setSaveStatus('idle')
    saveRequest.current += 1

    loadCaptureVideo(scanPackageId)
      .then((source) => {
        if (!active) {
          if (source?.revoke) source.revoke()
          return
        }
        applyVideoSource(source)
      })
      .catch(() => {
        if (!active) return
        applyVideoSource(null)
        setVideoError(true)
      })

    fetchFailureType(scanPackageId)
      .then((review) => {
        if (active) setFailureReview(review)
      })
      .catch(() => {
        if (active) setFailureReview(null)
      })

    return () => {
      active = false
      saveRequest.current += 1
      clearVideoObjectUrl()
    }
  }, [scanPackageId])

  const handleVideoError = () => {
    if (playbackRetries.current >= 2) {
      setVideoError(true)
      applyVideoSource(null)
      return
    }
    playbackRetries.current += 1
    loadVideo(true)
  }

  const changeFailureType = (failureType) => {
    const previous = failureReview.failureType
    const request = ++saveRequest.current
    setFailureReview({ ...failureReview, failureType })
    setSaveStatus('saving')
    updateFailureType(scanPackageId, failureType)
      .then((review) => {
        if (saveRequest.current !== request) return
        setFailureReview(review)
        setSaveStatus('saved')
        if (failureType) {
          onFailureTypeSaved?.(review)
        }
      })
      .catch(() => {
        if (saveRequest.current !== request) return
        setFailureReview({ ...failureReview, failureType: previous })
        setSaveStatus('error')
      })
  }

  const classification =
    failureReview === undefined ? (
      <div className="snap-failures-status">Loading labels…</div>
    ) : failureReview ? (
      <FailureTypeSelect
        failureType={failureReview.failureType}
        failureTypes={failureReview.failureTypes}
        onChange={changeFailureType}
        disabled={saveStatus === 'saving'}
        status={saveStatus}
        label={isSession ? 'Why did this fail?' : 'Failure classification'}
        hint={
          isSession
            ? 'Pick the main issue in the clip. Saving loads the next one.'
            : 'Choose the primary issue visible in the capture video. You can revise or clear the label later.'
        }
        emptyOptionLabel={isSession ? 'Choose a reason…' : 'Not reviewed'}
        selectClassName={
          isSession ? 'form-control form-control-lg' : 'form-control'
        }
        layout={isSession ? 'buttons' : 'select'}
        statusMessages={
          isSession ? sessionSaveStatusMessages : saveStatusMessages
        }
      />
    ) : (
      <div className="text-danger">Labels unavailable for this scan.</div>
    )

  const video =
    videoUrl === undefined ? (
      <Loading />
    ) : videoUrl ? (
      <video
        key={videoUrl}
        controls
        autoPlay
        muted
        playsInline
        preload="metadata"
        src={videoUrl}
        onError={handleVideoError}
        className={isSession ? 'snap-failures-video-el' : undefined}
        style={
          isSession
            ? undefined
            : { display: 'block', maxHeight: 600, width: '100%' }
        }
      >
        Your browser does not support video playback.
      </video>
    ) : (
      <div className="snap-failures-video-missing">
        <span className="fas fa-video-slash mb-2" aria-hidden="true" />
        <div>
          {videoError
            ? 'Capture video failed to load'
            : 'No capture video for this scan'}
        </div>
        {videoError ? (
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary mt-2"
            onClick={() => {
              playbackRetries.current = 0
              loadVideo(true)
            }}
          >
            Retry
          </button>
        ) : null}
      </div>
    )

  if (isSession) {
    return (
      <div className="snap-failures-stage">
        <aside className="snap-failures-panel" aria-label="Label this scan">
          {classification}
          {secondaryAction ? (
            <div className="snap-failures-secondary mt-3">
              {secondaryAction}
            </div>
          ) : null}
        </aside>
        <div className="snap-failures-media">{video}</div>
      </div>
    )
  }

  return (
    <div className="px-3 pt-3">
      <p className="text-muted">
        Privacy-filtered scanner footage for diagnosing failed scans.
      </p>
      {video}
      <div className="mt-3">{classification}</div>
    </div>
  )
}

CaptureVideo.propTypes = {
  scanPackageId: PropTypes.string.isRequired,
  onFailureTypeSaved: PropTypes.func,
  variant: PropTypes.oneOf(['detail', 'session']),
  secondaryAction: PropTypes.node,
}
